import type { Card } from '@/types';
import type {
  TutorContext,
  TutorConcept,
  TutorCurriculumItem,
  TutorExercise,
  TutorSession,
  CEFRLevel,
} from '@/types/tutor';
import type { GrammarTopicProgress } from '@/types/quiz';
import { fsrs, generatorParameters, type CardInput } from 'ts-fsrs';

export interface ContextBuilderInput {
  cefrLevel: CEFRLevel | null;
  cards: Card[];
  grammarProgress: GrammarTopicProgress[];
  tutorConcepts: TutorConcept[];
  curriculum: TutorCurriculumItem[];
  recentSessions: TutorSession[];
  recentExercises: TutorExercise[];
}

export function buildTutorContext(input: ContextBuilderInput): TutorContext {
  return {
    cefrLevel: input.cefrLevel,
    knownVocab: input.cards
      .filter((c) => c.cardType === 'word' || c.cardType === 'phrase')
      .map((c) => c.targetWord)
      .slice(0, 50),
    strugglingVocab: extractStrugglingVocab(input.cards),
    grammarProgress: input.grammarProgress
      .filter((p) => p.totalAttempts > 0)
      .map((p) => ({
        guideId: p.guideId,
        sectionIndex: p.sectionIndex,
        accuracy: Math.round((p.correctAttempts / p.totalAttempts) * 100),
      })),
    curriculum: input.curriculum.map((item) => ({
      conceptKey: item.conceptKey,
      status: item.status,
      masteryLevel:
        item.status === 'mastered'
          ? 3
          : item.status === 'reviewing'
            ? 2
            : item.status === 'practicing'
              ? 1
              : 0,
    })),
    recentMistakes: extractRecentMistakes(input.recentExercises),
    recentSessions: input.recentSessions
      .slice(0, 3)
      .map((s) => ({ summary: s.sessionSummary, conceptKey: s.conceptKey })),
  };
}

function extractStrugglingVocab(cards: Card[]): string[] {
  const now = new Date();
  const f = fsrs(generatorParameters());
  return cards
    .filter((c) => {
      if (!c.srsState) return false;
      try {
        const r = f.get_retrievability(c.srsState as CardInput, now, false);
        return r < 0.5;
      } catch {
        return false;
      }
    })
    .map((c) => c.targetWord)
    .slice(0, 20);
}

function extractRecentMistakes(
  exercises: TutorExercise[],
): { conceptKey: string; mistake: string; count: number }[] {
  const mistakes = new Map<string, { mistake: string; count: number }>();
  for (const ex of exercises) {
    if (!ex.userAnswers || ex.score === null || ex.score >= 80) continue;
    const wrong = ex.userAnswers.filter((a) => !a.isCorrect);
    for (const ans of wrong.slice(0, 3)) {
      const key = `${ex.conceptKey}::${ans.questionId}`;
      const existing = mistakes.get(key);
      if (existing) existing.count += 1;
      else
        mistakes.set(key, {
          mistake: `${ex.conceptKey}: "${ans.userAnswer}" (correct: "${ans.correctAnswer}")`,
          count: 1,
        });
    }
  }
  return Array.from(mistakes.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
    .map((m) => ({ conceptKey: m.mistake.split(':')[0], mistake: m.mistake, count: m.count }));
}
